import React from 'react';
import { Platform, SectionList, SectionListProps } from 'react-native';
import styled from 'styled-components/native';
import ListItem from '../../components/list/ListItem';
import ListItemAccessory from '../../components/list/ListItemAccessory';
import ListItemIcon from '../../components/list/ListItemIcon';
import ListItemText from '../../components/list/ListItemText';
import Spacer from '../../components/Spacer';
import { B, H4, Subtitle } from '../../components/Typography';
import Colors from '../../constants/Colors';

import { format } from 'date-fns';

const StyleContainer = styled.View`
  background-color: ${Colors.background};
  ${Platform.OS === 'web' ? 'margin-top: 70px' : ''};
`;
const StyledSectionList = styled.SectionList`
  padding: 0 12px;
` as React.FC<SectionListProps<(typeof activities)[number]>>;

const StyledHeaderView = styled.View`
  background: ${Colors.background};
  padding: 24px 8px 18px 8px;
`;

const StyledRectangleImage = styled.Image`
  height: 43.29px;
  width: 49px;
  border-radius: 8px;
`;

const StyledSquareImage = styled.Image`
  height: 35px;
  width: 35px;
  border-radius: 12px;
`;

const StyledSubtitle = styled(Subtitle)`
  color: ${Colors.secondaryText};
`;

const StyledView = styled.View`
  width: 100%;
  flex-direction: row;
  justify-content: space-between;
`;

const StyledB = styled(B)`
  color: ${Colors.secondaryText};
`;

const sleepStudies: {
  title?: string;
  frequency?: string;
  imageUrl?: string;
}[] = [
  {
    title: 'Magnesium Supplement',
    frequency: '3 Weeks',
    imageUrl: 'https://i.postimg.cc/HknR8Rq7/sleep-icon-fixed.png'
  },
  {
    title: 'CBD for Sleep',
    frequency: '4 Weeks',
    imageUrl: 'https://i.postimg.cc/HknR8Rq7/sleep-icon-fixed.png'
  },
  {
    title: 'Tart Cherry Juice',
    frequency: '2 Weeks',
    imageUrl: 'https://i.postimg.cc/HknR8Rq7/sleep-icon-fixed.png'
  }
];

const painStudies: {
  title?: string;
  frequency?: string;
  imageUrl?: string;
}[] = [
  {
    title: 'Turmeric',
    frequency: '3 Weeks',
    imageUrl: 'https://i.postimg.cc/1RDYFNyG/AT23sd3sd73.png'
  }
];

const skinStudies: {
  title?: string;
  frequency?: string;
  imageUrl?: string;
}[] = [
  {
    title: 'Mud Mask',
    frequency: '3 Weeks',
    imageUrl: 'https://i.postimg.cc/zfbrWSDz/dhs63hcs633.png'
  },
  {
    title: 'Probiotic Cream',
    frequency: '3 Weeks',
    imageUrl: 'https://i.postimg.cc/zfbrWSDz/dhs63hcs633.png'
  }
];

const expiringSoon: {
  title?: string;
  date?: Date;
  imageUrl?: string;
}[] = [
  {
    title: 'Track Sleep',
    date: new Date('11/11/2022'),
    imageUrl: 'https://i.postimg.cc/Kv7PJxrR/hsg2h4hf.png'
  },
  {
    title: 'Track Sleep',
    date: new Date('11/12/2022'),
    imageUrl: 'https://i.postimg.cc/Kv7PJxrR/hsg2h4hf.png'
  },
  {
    title: 'Track Sleep',
    date: new Date('11/13/2022'),
    imageUrl: 'https://i.postimg.cc/Kv7PJxrR/hsg2h4hf.png'
  }
];

const activities: {
  title?: string;
  durationMin?: number;
  imageUrl?: string;
}[] = [
  {
    title: 'Track Sleep',
    durationMin: 5,
    imageUrl: 'https://i.postimg.cc/Kv7PJxrR/hsg2h4hf.png'
  },
  {
    title: 'Weekly Survey',
    durationMin: 10,
    imageUrl: 'https://i.postimg.cc/t4C9V4my/ts2ks4jsfs.png'
  }
];

const ExampleListScreen: React.FC = () => {
  return (
    <StyleContainer>
      <StyledSectionList
        renderSectionHeader={({ section }) => {
          if (!section.heading) return null;
          return (
            <StyledHeaderView>
              <H4>{section.heading}</H4>
            </StyledHeaderView>
          );
        }}
        sections={[
          {
            heading: (
              <StyledView>
                <StyledSubtitle>Activities</StyledSubtitle>
              </StyledView>
            ),
            data: activities,
            keyExtractor: (_, index) => index.toString(),
            renderItem: ({ item }) => (
              <ListItem framed divider filled>
                <ListItemIcon iconPosition='left'>
                  <StyledSquareImage
                    resizeMode='contain'
                    source={{ uri: item.imageUrl }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={item.title}
                  primaryTextColor={Colors.secondaryText}
                />
                <ListItemAccessory>
                  <StyledB color='dark'>{item.durationMin} min</StyledB>
                </ListItemAccessory>
              </ListItem>
            ),
            ItemSeparatorComponent: () => <Spacer />
          },
          {
            heading: (
              <StyledView>
                <StyledSubtitle>Expiring Soon</StyledSubtitle>
              </StyledView>
            ),
            data: expiringSoon,
            keyExtractor: (_, index) => index.toString(),
            renderItem: ({ item }) => (
              <ListItem framed divider filled>
                <ListItemIcon iconPosition='left'>
                  <StyledSquareImage
                    resizeMode='contain'
                    source={{ uri: item.imageUrl }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={item.title}
                  primaryTextColor={Colors.secondaryText}
                />
                <ListItemAccessory>
                  <StyledB color='dark'>{format(item.date, 'MMM dd')}</StyledB>
                </ListItemAccessory>
              </ListItem>
            ),
            ItemSeparatorComponent: () => <Spacer />
          },
          {
            heading: (
              <StyledView>
                <StyledSubtitle>Sleep Studies</StyledSubtitle>
              </StyledView>
            ),
            data: sleepStudies,
            keyExtractor: (_, index) => index.toString(),
            renderItem: ({ item }) => (
              <ListItem framed divider filled>
                <ListItemText
                  primary={item.title}
                  primaryTextColor={Colors.secondaryText}
                  largeSecondary={`${item.frequency}`}
                  secondaryTextColor={'#5B4FC2'}
                />
                <ListItemIcon iconPosition='right'>
                  <StyledRectangleImage
                    resizeMode='contain'
                    source={{ uri: item.imageUrl }}
                  />
                </ListItemIcon>
              </ListItem>
            ),
            ItemSeparatorComponent: () => <Spacer />
          },
          {
            heading: (
              <StyledView>
                <StyledSubtitle>Pain Studies</StyledSubtitle>
              </StyledView>
            ),
            data: painStudies,
            keyExtractor: (_, index) => index.toString(),
            renderItem: ({ item }) => (
              <ListItem framed divider filled type='study'>
                <ListItemText
                  primary={item.title}
                  primaryTextColor={Colors.secondaryText}
                  largeSecondary={`${item.frequency}`}
                  secondaryTextColor={'#5B4FC2'}
                />
                <ListItemIcon iconPosition='right'>
                  <StyledRectangleImage
                    resizeMode='contain'
                    source={{ uri: item.imageUrl }}
                  />
                </ListItemIcon>
              </ListItem>
            ),
            ItemSeparatorComponent: () => <Spacer />
          },
          {
            heading: (
              <StyledView>
                <StyledSubtitle>Skin Studies</StyledSubtitle>
              </StyledView>
            ),
            data: skinStudies,
            keyExtractor: (_, index) => index.toString(),
            renderItem: ({ item }) => (
              <ListItem framed divider filled type='study'>
                <ListItemText
                  primary={item.title}
                  primaryTextColor={Colors.secondaryText}
                  largeSecondary={`${item.frequency}`}
                  secondaryTextColor={'#5B4FC2'}
                />
                <ListItemIcon iconPosition='right'>
                  <StyledRectangleImage
                    resizeMode='contain'
                    source={{ uri: item.imageUrl }}
                  />
                </ListItemIcon>
              </ListItem>
            ),
            ItemSeparatorComponent: () => <Spacer />
          }
        ]}
      />
    </StyleContainer>
  );
};

export default ExampleListScreen;
